import React from 'react';
import { CompletionGuard, PluginRegistration } from '@/infra/plugin-registry';
import { projectsReducer } from './ducks';
import { projectsSaga } from './sagas';
import { ProjectTodoDetailSection } from './components/ProjectTodoDetailSection';
import { InboxProjectsView } from './components/InboxProjectsView';
import { selectChildrenMap } from './selectors';

const LazyProjectsPage = React.lazy(
  () => import('./pages/ProjectsPage')
);

// Veto Complete in the detail modal when the todo has any open subtasks.
// Mirrors the server's PARENT_HAS_OPEN_CHILDREN guard so the user sees the
// disabled state up front instead of clicking, getting a 409, and reverting.
// Returns null when the project's tree isn't loaded — the server still
// rejects, the outline error banner surfaces it.
const completionGuard: CompletionGuard = (state, todo) => {
  if (todo.status === 'done' || todo.status === 'cancelled') return null;
  const childrenMap = selectChildrenMap(state as { projects: ReturnType<typeof projectsReducer> });
  const children = childrenMap[todo.id] ?? [];
  if (children.length === 0) return null;
  const open = children.filter(
    (c) => c.status !== 'done' && c.status !== 'cancelled',
  ).length;
  if (open === 0) return null;
  return `Complete ${open} subtask${open === 1 ? '' : 's'} before closing this task.`;
};

export const projectsManifest: PluginRegistration = {
  id: 'projects',
  display_name: 'Projects',
  description: 'Group todos with a subtask hierarchy; parents close only when children do',
  version: '1.0.0',
  enabled_by_default: true,
  todo_types: [
    {
      type_tag: 'projects.task',
      label: 'Project Task',
    },
  ],
  web_routes: [
    {
      path: '/projects',
      sidebar_label: 'Projects',
      sidebar_order: 1,
    },
    {
      path: '/projects/:id',
      sidebar_label: '',
      sidebar_order: -1,
    },
  ],
  reducer: projectsReducer,
  saga: projectsSaga,
  routes: [LazyProjectsPage, LazyProjectsPage],
  detailSlots: {
    'below-title': ProjectTodoDetailSection,
  },
  // Contributes the "Projects" sub-tab to the Inbox: all active projects'
  // full task hierarchy, read-only + schedule-drag. Honored only while
  // Projects is enabled.
  inboxSections: [
    { id: 'projects', label: 'Projects', order: 0, render: InboxProjectsView },
  ],
  completionGuard,
  // Project tasks are owned by Projects, not the default inbox: exclude them
  // from the inbox list so they only surface via the "Show project tasks"
  // section (and the project outline). Only applied while Projects is enabled.
  inboxClaim: (todo) => todo.project_id != null,
};
